import React from 'react';
import styled, { css } from 'styled-components';
import { CardRow } from '../../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../../generic/ui/Mono';
import Checkbox from '../../../knisot/Create/Components/Checkbox';
import InvoiceIcon from '../../../../../../generic/icons/IconLib/InvoiceIcon';
import BasketIcon from '../../../../../../generic/icons/IconLib/BasketIcon';
import PalletIcon from '../../../../../../generic/icons/IconLib/PalletIcon';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import { IOrderItem } from '../../../../../../modules/API/Drivers/models/kavData';
import { aggregateOrders } from '../../helpers/aggregate';
import { getOrderDeliveryState, OrderDeliveryState } from '../../helpers/deliveryActions';
import { DriversTag, IconSlot, IdText } from '../../Components/DriversCardParts';
import StatusReturningBadge from '../../Components/StatusReturningBadge';

export interface DeliveryOrderRowProps {
    order: IOrderItem;
    onToggle: (order: IOrderItem) => void;
}

/**
 * Строка теуды в модалке подтверждения доставки: выбор переключает и клик по
 * строке, и сам чекбокс (паттерн LikutGoodsRow — слот гасит всплытие, чтобы
 * не переключить дважды).
 */
const DeliveryOrderRow: React.FC<DeliveryOrderRowProps> = ({ order, onToggle }) => {
    const isSelected = !!order.Selected;
    const state = getOrderDeliveryState(order);
    const { pallets } = aggregateOrders([order]);
    const group = order.GroupDesc?.trim();
    const note = order.DistributNote?.trim();

    const toggle = () => onToggle(order);

    return (
        <Row
            $state={state}
            $selected={isSelected}
            $columns='auto minmax(0, 1fr)'
            $mobileColumns='auto minmax(0, 1fr)'
            $minHeight='auto'
            $padding='0.625rem 0.875rem'
            $gap='0.75rem'
            $hover={state === 'none'}
            $interactive
            onClick={toggle}
        >
            <CardRow.Cell $align='center'>
                <CheckboxSlot onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isSelected} onChange={toggle} />
                </CheckboxSlot>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Stack $gap='0.25rem'>
                    <CardRow.Line $gap='0.375rem' $wrap>
                        <IconSlot $muted>
                            <InvoiceIcon size={14} />
                        </IconSlot>
                        <IdText>{order.Delivery_Num}</IdText>
                        <IconSlot $muted>
                            <BasketIcon size={14} />
                        </IconSlot>
                        <Meta>{order.OrderID}</Meta>
                        {group && <DriversTag variant='tinted'>{group}</DriversTag>}
                    </CardRow.Line>

                    <CardRow.Line $gap='0.375rem' $wrap>
                        <IconSlot $muted>
                            <PalletIcon size={13} />
                        </IconSlot>
                        <Meta>{pallets.toFixed(2)}</Meta>
                        {order.IsProblem && <StatusReturningBadge status={order.DriverStatus} />}
                    </CardRow.Line>

                    {note && (
                        <NoteLine>
                            <PencilIcon size={12} />
                            <NoteText>{note}</NoteText>
                        </NoteLine>
                    )}
                </CardRow.Stack>
            </CardRow.Cell>
        </Row>
    );
};

export default DeliveryOrderRow;

/** Состояние строки — пастельной заливкой, как edgeFill у OrderTile. */
const stateFill = {
    none: null,
    delivered: 'successSoft',
    failed: 'dangerSoft',
} as const;

const Row = styled(CardRow)<{ $state: OrderDeliveryState; $selected: boolean }>`
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};

    ${({ $state, theme }) => {
        const fill = stateFill[$state];
        return (
            fill &&
            css`
                background: ${theme.colors[fill]};
            `
        );
    }}

    ${({ $selected, theme }) =>
        $selected &&
        css`
            border-color: ${theme.colors.accentDeep};
            box-shadow: inset 0 0 0 1px ${theme.colors.accentDeep};
        `}
`;

const CheckboxSlot = styled.span`
    display: inline-flex;
`;

const Meta = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const NoteLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    color: ${({ theme }) => theme.colors.danger};
`;

const NoteText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.danger};
    overflow-wrap: anywhere;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;
